import React, { useEffect, useState } from "react";
import { api, formatApiError, statoLabel } from "../lib/api";
import { X } from "lucide-react";
import { toast } from "sonner";

const BLANK = {
  nome: "", cognome_ragione_sociale: "", telefono: "", email: "",
  indirizzo: "", citta: "", cap: "", provincia: "", regione: "", cf_piva: "",
  note: "", commessa_id: "", stato_pratica: "in_lavorazione",
};

const STATI = ["bozza", "in_lavorazione", "in_corso", "completato", "sospeso", "annullato"];

export const REGIONI = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
  "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana",
  "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto",
];

/**
 * Modal di create/edit anagrafica cliente — riusabile tra la lista (/clienti) e la scheda (/clienti/{id}).
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - cliente: cliente da modificare oppure null per "nuovo"
 *  - commesse: lista commesse selezionabili
 *  - onSaved: (savedCliente) => void   — chiamato dopo il salvataggio per ricaricare i dati nel parent
 */
export default function ClienteFormModal({ open, onClose, cliente, commesse, onSaved }) {
  const editing = !!cliente;
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (cliente) {
      setForm({
        nome: cliente.nome || "",
        cognome_ragione_sociale: cliente.cognome_ragione_sociale || "",
        telefono: cliente.telefono || "",
        email: cliente.email || "",
        indirizzo: cliente.indirizzo || "",
        citta: cliente.citta || "",
        cap: cliente.cap || "",
        provincia: cliente.provincia || "",
        regione: cliente.regione || "",
        cf_piva: cliente.cf_piva || "",
        note: cliente.note || "",
        commessa_id: cliente.commessa_id || "",
        stato_pratica: cliente.stato_pratica || "bozza",
      });
    } else {
      setForm({ ...BLANK, commessa_id: (commesse && commesse[0]?.id) || "" });
    }
  }, [open, cliente, commesse]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = editing
        ? await api.put(`/clienti/${cliente.id}`, form)
        : await api.post("/clienti", form);
      toast.success(editing ? "Cliente aggiornato" : "Cliente creato");
      onSaved?.(res.data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black/40 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="card-shell w-full max-w-3xl my-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="cliente-form-modal"
      >
        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>
            {editing ? "Modifica cliente" : "Nuovo cliente"}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5" data-testid="cliente-form-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["nome", "Nome *", true], ["cognome_ragione_sociale", "Cognome / Ragione sociale *", true],
            ["telefono", "Telefono"], ["email", "Email"],
            ["indirizzo", "Indirizzo"], ["citta", "Città"],
            ["cap", "CAP"], ["provincia", "Provincia *", true],
            ["cf_piva", "CF / P.IVA"],
          ].map(([k, l, req]) => (
            <div key={k}>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{l}</label>
              <input
                data-testid={`form-${k}`}
                value={form[k] || ""}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                required={!!req}
                className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
                style={{ borderColor: "var(--border)", background: "#fff" }}
              />
            </div>
          ))}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Regione *</label>
            <select
              data-testid="form-regione"
              value={form.regione || ""}
              onChange={(e) => setForm({ ...form, regione: e.target.value })}
              required
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
            >
              <option value="">— Seleziona regione —</option>
              {REGIONI.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Commessa *</label>
            <select
              data-testid="form-commessa"
              value={form.commessa_id}
              onChange={(e) => setForm({ ...form, commessa_id: e.target.value })}
              required
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
            >
              {(commesse || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Stato pratica</label>
            <select
              data-testid="form-stato"
              value={form.stato_pratica}
              onChange={(e) => setForm({ ...form, stato_pratica: e.target.value })}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
            >
              {STATI.map((s) => <option key={s} value={s}>{statoLabel(s)}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Note</label>
            <textarea
              data-testid="form-note"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              rows={3}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
            />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Annulla</button>
            <button type="submit" className="btn-primary" data-testid="form-submit" disabled={saving}>
              {saving ? "Salvataggio…" : editing ? "Aggiorna" : "Crea cliente"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
